/* eslint-disable react/no-children-prop */
/* eslint-disable max-len */
/* eslint-disable no-console */
import React, { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
	Avatar,
	Box,
	Button,
	Checkbox,
	Chip,
	Collapse,
	FormHelperText,
	Grid,
	IconButton,
	InputAdornment,
	Popover,
	Stack,
	TextField,
	Typography
} from '@mui/material';
import { LanguageContext } from '../../../libs/Context';
import { catchErr, queryParams } from '../../../libs/HelperContent';
import { useSelector } from 'react-redux';
import { getRelationsCounts } from '../../../containers/actions/meeting_actions';
import { getConnectionswithRelationId } from '../../../containers/actions/new_connections_action';
import { getEmployeesData } from '../../../containers/actions/employee_actions';
import { deptEmployeeCount } from '../../../containers/actions/department_actions';
import useDebounce from '../../Hooks/useDebounce';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { MdOutlinePeople } from 'react-icons/md';
import { ValidateEmail } from '../../../libs/Validation';
import Loading from '../../Common/Loaders/FullWidthLoader/Loading';
import NoData from '../../Common/NoData';
import useInterSectionObserver from '../../../libs/CustomHooks/useIntersectionObserver';
import { IoClose } from 'react-icons/io5';
import { toast } from 'react-toastify';

interface Participant {
	userId?: string;
	email: string;
	name?: string;
	ava?: string;
	department?: string;
	relationId?: string;
	relationName?: string;
	type?: string;
	relationType?: string;
}

interface Relation {
	relationId: string;
	relationName: string;
	count: number;
	relation?: string;
	type?: string;
	relationType?: string;
}

const ParticipantsSelector = forwardRef((props: any, ref) =>
{
	const {
		setFormError,
		formError,
		noOfSeats,
		participantCount,
		shareTo,
		participants
	} = props;

	console.log('shareTo', shareTo);

	const { language } = useContext(LanguageContext);
	const userData = useSelector((state: any) => state?.mysession?.session);

	// State
	const [ open, setOpen ] = useState(false);
	const [ relations, setRelations ] = useState<Relation[]>([]);
	const [ selectedRelations, setSelectedRelations ] = useState<Relation[]>([]);
	const [ selectedDepartment, setSelectedDepartment ] = useState<string>('');
	const [ departmentsList, setDepartmentsList ] = useState<any[]>([]);
	const [ selectedEmails, setSelectedEmails ] = useState<string[]>([]);
	const [ selectedParticipants, setSelectedParticipants ] = useState<Participant[]>([]); // Store full participant objects with relation info
	const [ participantsList, setParticipantsList ] = useState<Participant[]>([]);
	const [ searchVal, setSearchVal ] = useState<string>('');
	const [ activeTab, setActiveTab ] = useState<string>('');
	const [ selectedParticipantsTab, setSelectedParticipantsTab ] = useState<string>(''); // Tab for selected participants view
	const [ page, setPage ] = useState<number>(0);
	const [ loading, setLoading ] = useState<boolean>(false);
	// const [ emailInput, setEmailInput ] = useState<string>('');
	const [ hasMoreData, setHasMoreData ] = useState<boolean>(true);
	const [ anchorEl, setAnchorEl ] = useState<HTMLElement | null>(null);
	const [ selectedRelationForDialog, setSelectedRelationForDialog ] = useState<string>('');
	const [ selectAllUsedForRelations, setSelectAllUsedForRelations ] = useState<Record<string, boolean>>({}); // Track which relations had "Select All" used

	// Refs
	const loaderRef = useRef<any>(false);
	const lastEleRef = useRef<any>();
	const debouncedSearch = useDebounce(searchVal, 500);
	const debouncedCount = useDebounce(noOfSeats, 300);
	const seatRef = useRef<any>('2');

	// Pagination hook - use a large number for count if hasMoreData is true
	const LoadMoreList = useInterSectionObserver({
		lastEleRef,
		loaderRef,
		count : hasMoreData ? Number.MAX_SAFE_INTEGER : participantsList.length,
		data  : participantsList,
		setPage
	});

	const capitalizeWords = (text = '') =>
		text.replace(/\b\w/g, (char) => char.toUpperCase());

	// Get relations (categories)
	const getRelations = async () =>
	{
		try
		{
			const query: any = {
				uid     : userData?.userId,
				comid   : userData?.companyId,
				empRole : userData?.empRole,
				role    : userData?.role
			};

			const res = await getRelationsCounts(query);

			if (res?.status)
			{
				const relationsData = res?.data || [];

				// Sort relations: Internal Employees first, then others
				const sortedRelations = [ ...relationsData ].sort((a: Relation, b: Relation) =>
				{
					const aIsInternal = a.relationName.toLowerCase().includes('internal');
					const bIsInternal = b.relationName.toLowerCase().includes('internal');

					if (aIsInternal && !bIsInternal) return -1;

					if (!aIsInternal && bIsInternal) return 1;

					return 0;
				});

				setRelations(sortedRelations);

				// Set default activeTab to Internal Employees (fallback to first relation)
				if (sortedRelations.length > 0 && !activeTab)
				{
					const internalEmployeesRelation = sortedRelations.find((r: Relation) =>
						r.relationName.toLowerCase().includes('internal'));

					if (internalEmployeesRelation)
					{
						// Default to the Internal Employees tab
						setActiveTab(internalEmployeesRelation.relationName.toLowerCase());
					}
					else
					{
						// If no Internal Employees relation exists, use the first relation
						setActiveTab(sortedRelations[0].relationName.toLowerCase());
					}
				}
			}
		}
		catch (err)
		{
			catchErr(err);
		}
	};

	// Get departments list
	const getDepartmentsList = async () =>
	{
		try
		{
			const eachquery = { companyId: userData?.companyId, module: 'presence', ignoreSelf: true };
			const res = await deptEmployeeCount(eachquery);
			if (res?.status)
			{
				const depts = (res?.data || [])
					.filter((d: any) => (d?.dept || d?.name || d) !== 'All')
					.map((d: any) => ({
						name          : d?.dept || d?.name || d,
						employeeCount : d?.empCount ?? d?.employeeCount ?? d?.count ?? 0
					}));
				setDepartmentsList(depts);
			}
		}
		catch (err)
		{
			// eslint-disable-next-line no-console
			console.log('err------>', err);
		}
	};

	// Get participants list with pagination - filters by activeTab
	const getParticipantsList = async (resetPage = false) =>
	{
		const currentPage = resetPage ? 0 : page;

		setLoading(currentPage === 0);

		try
		{
			// Find the selected relation based on activeTab
			if (!activeTab)
			{
				setLoading(false);

				return;
			}

			const selectedRelation = relations.find((r: Relation) =>
			{
				const relationNameLower = r.relationName.toLowerCase();
				const activeTabLower = activeTab.toLowerCase();

				return relationNameLower === activeTabLower ||
					(activeTabLower === 'internal employees' && relationNameLower.includes('internal'));
			});

			if (!selectedRelation)
			{
				setLoading(false);

				return;
			}

			const isInternalEmployees = selectedRelation.relationName.toLowerCase().includes('internal');
			let res: any;

			// For Internal Employees, use getEmployeesData API
			if (isInternalEmployees)
			{
				const queryParamRes = queryParams(userData?.userId, userData?.companyId, userData?.empRole, userData?.role, 'get');
				// skip should be page number (0, 1, 2, 3...) not multiplied by 10
				// limit is always 10
				let queryItem = `?status=approve&limit=10&skip=${currentPage}${queryParamRes}`;

				// Add search query if available
				if (searchVal)
				{
					queryItem += `&search=${encodeURIComponent(searchVal)}`;
				}

				// Add department filter if a department is selected (and not empty or 'All')
				// Use the current state value directly to avoid stale closure issues
				const currentSelectedDept = selectedDepartment;

				if (currentSelectedDept && currentSelectedDept !== '' && currentSelectedDept !== 'All')
				{
					queryItem += `&departments=${encodeURIComponent(currentSelectedDept)}`;
				}

				res = await getEmployeesData(queryItem);
			}
			else
			{
				// For other relations, use getConnectionswithRelationId API with
				// page-wise pagination (skip = page index, limit = 10) so infinite
				// scroll works the same way as the Internal Employees (getEmployeesData) flow.
				const query: any = {
					relation : selectedRelation.relationId, // Only send relationId as 'relation' parameter
					limit    : 10,
					skip     : currentPage
				};

				// Add search query if available
				if (searchVal)
				{
					query.search = searchVal;
				}

				res = await getConnectionswithRelationId(query);
			}

			if (res?.status && res?.data)
			{
				// Transform to participant format
				const newParticipants = (res.data || []).map((item: any) =>
				{
					// Return object with userId, name, and email
					return {
						userId     : item.userId,
						name       : item.name || item.fullName || '',
						email      : item.email || item.userId, // Use userId if email not available
						ava        : item.ava || item.profileImg || item.profileImage || item.profileImageUrl || '',
						department : item.department || item.departmentName || '',
						type       : item.type || item.relationType || ''
					};
				});

				// A full page (10 items) means there may be more to load. This now applies
				// to both the Internal Employees (getEmployeesData) and the relation
				// connections (getConnectionswithRelationId) flows, since both paginate.
				setHasMoreData(newParticipants.length === 10);

				if (resetPage || currentPage === 0)
				{
					setParticipantsList(newParticipants);
				}
				else
				{
					setParticipantsList((prev) => [ ...prev, ...newParticipants ]);
				}

				setLoading(false);
				loaderRef.current = false;
			}
			else
			{
				setHasMoreData(false);
				setLoading(false);
				loaderRef.current = false;
			}
		}
		catch (err)
		{
			catchErr(err);
			setLoading(false);
			loaderRef.current = false;
		}
	};

	// Calculate total selected count
	const getTotalSelectedCount = () =>
	{
		// selectedParticipants already contains all selected participants:
		// - All participants from fully selected relation groups
		// - Individual participants selected from any relation
		// - External emails added manually
		// This prevents double counting when both a relation group and individuals are selected
		return selectedParticipants.length;
	};

	// Get available seats
	const getAvailableSeats = () =>
	{
		const totalSeats = Number(noOfSeats);
		const currentCount = getTotalSelectedCount();

		return totalSeats - currentCount;
	};

	const participantKeys = (p: Participant | string): string[] =>
	{
		if (typeof p === 'string') return [ p ];

		if (p && typeof p === 'object')
		{
			return [ p.userId, p.email ].filter((v): v is string => Boolean(v));
		}

		return [];
	};

	// Check if a participant is already selected, matching by userId OR email
	const isParticipantSelected = (participant: Participant | string): boolean =>
	{
		const keys = participantKeys(participant);

		if (keys.length === 0) return false;

		if (keys.some((k) => selectedEmails.includes(k))) return true;

		return selectedParticipants.some((sp) =>
			participantKeys(sp).some((k) => keys.includes(k))
		);
	};

	// Handle relation selection
	const handleRelationToggle = (relation: Relation) =>
	{
		const isSelected = selectedRelations.some((r: Relation) => r.relationId === relation.relationId);

		if (isSelected)
		{
			// Deselecting a relation group
			setSelectedRelations((prev) => prev.filter((r: Relation) => r.relationId !== relation.relationId));

			// Remove all participants from this relation
			setSelectedParticipants((prev) => prev.filter((p: Participant) =>
				p.relationId !== relation.relationId
			));

			// Remove emails of participants from this relation
			setSelectedEmails((prev) =>
			{
				const participantsToRemove = selectedParticipants
					.filter((p: Participant) => p.relationId === relation.relationId)
					.map((p: Participant) => p.email || p.userId || '');

				return prev.filter((email) => !participantsToRemove.includes(email));
			});

			// If Internal Employees deselected, clear department
			if (relation.relationName.toLowerCase().includes('internal'))
			{
				setSelectedDepartment('');
			}
		}
		else
		{
			// Selecting a relation group - validate seat limit.
			// Exclude individuals already selected from this relation, since they
			// are part of relation.count and would otherwise be counted twice.
			const alreadySelectedFromRelation = selectedParticipants.filter((p: Participant) =>
				p.relationId === relation.relationId
			).length;

			// Calculate what the new total would be after adding this relation
			const totalCount = getTotalSelectedCount() - alreadySelectedFromRelation + relation.count;

			if (totalCount <= Number(noOfSeats))
			{
				setSelectedRelations((prev) => [ ...prev, relation ]);
				setFormError({});
			}
			else
			{
				const errorMsg = language.participantsCannotExceedTotalSeats || 'Participants cannot exceed total seats';

				setFormError({ participant: errorMsg });
				toast.info(errorMsg, { position: 'top-right', autoClose: 3000 });
				setTimeout(() => { setFormError({}); }, 2000);
			}
		}
	};

	// Handle department selection (for Internal Employees) - Only one department can be selected
	const handleDepartmentToggle = (deptName: string) =>
	{
		// If clicking the same department, deselect it. Otherwise, select the new one
		const newDepartment = selectedDepartment === deptName ? '' : deptName;

		// Update department - useEffect will handle reload
		setSelectedDepartment(newDepartment);
	};

	// Handle participant selection
	const handleParticipantToggle = (participant: Participant | string) =>
	{
		try
		{
			// Extract identifier (email or userId) from participant
			let identifier: string = '';
			let participantObj: Participant | null = null;

			if (typeof participant === 'string')
			{
				identifier = participant;
				participantObj = {
					email : identifier,
					name  : identifier.split('@')[0]
				};
			}
			else if (participant && typeof participant === 'object')
			{
				// Use email if available, otherwise use userId
				identifier = participant.email || participant.userId || String(participant);
				participantObj = {
					...participant,
					email : participant.email || identifier
				};
			}
			else
			{
				identifier = String(participant || '');
			}

			if (!identifier || typeof identifier !== 'string')
			{
				console.error('No valid identifier found for participant:', participant);

				return;
			}

			const isSelected = isParticipantSelected(participantObj || identifier);

			if (isSelected)
			{
				const keys = participantKeys(participantObj || identifier);

				setSelectedEmails((prev) => prev.filter((e) => !keys.includes(e)));
				setSelectedParticipants((prev) => prev.filter((p) =>
					!participantKeys(p).some((k) => keys.includes(k))));
			}
			else
			{
				const availableSeats = getAvailableSeats();

				if (availableSeats <= 0)
				{
					const errorMsg = language.participantsCannotExceedTotalSeats || 'Participants cannot exceed total seats';

					setFormError({ participant: errorMsg });
					toast.info(errorMsg, { position: 'top-right', autoClose: 3000 });
					setTimeout(() => { setFormError({}); }, 3000);

					return;
				}

				// Get current relation info
				const selectedRelation = relations.find((r: Relation) =>
				{
					const relationNameLower = r.relationName.toLowerCase();
					const activeTabLower = activeTab.toLowerCase();

					return relationNameLower === activeTabLower ||
						(activeTabLower === 'internal employees' && relationNameLower.includes('internal'));
				});

				// Add participant with relation info
				const participantWithRelation: Participant = {
					...participantObj,
					email        : participantObj?.email || identifier,
					relationId   : selectedRelation?.relationId || participantObj?.userId || '',
					relationName : selectedRelation?.relationName || activeTab
				};

				setSelectedEmails((prev) => [ ...prev, identifier ]);
				setSelectedParticipants((prev) => [ ...prev, participantWithRelation ]);
				setFormError({});
			}
		}
		catch (error)
		{
			console.error('Error in handleParticipantToggle:', error);
			catchErr(error);
		}
	};

	// Handle email chip delete
	const handleEmailDelete = (email: string) =>
	{
		setSelectedEmails((prev) => prev.filter((e) => e !== email));
		setSelectedParticipants((prev) => prev.filter((p) => (p.email || p.userId) !== email));
		setFormError({});
	};

	// Handle participant delete from selected list
	const handleParticipantDelete = (participant: Participant) =>
	{
		const identifier = participant.email || participant.userId || '';

		setSelectedEmails((prev) => prev.filter((e) => e !== identifier));
		setSelectedParticipants((prev) => prev.filter((p) =>
			(p.email || p.userId) !== identifier));
		setFormError({});
	};

	// Get participants grouped by relation
	const getParticipantsByRelation = () =>
	{
		const grouped: Record<string, Participant[]> = {};

		selectedParticipants.forEach((participant) =>
		{
			const relationName = participant.relationName || 'Others';
			const relationKey = relationName.toLowerCase();

			if (!grouped[relationKey])
			{
				grouped[relationKey] = [];
			}

			grouped[relationKey].push(participant);
		});

		return grouped;
	};

	// Get relation tabs for selected participants - includes both relation groups and individual participants
	const getSelectedParticipantsTabs = () =>
	{
		const tabs: Array<{ name: string; label: string; count: number; isGroup: boolean }> = [];

		// First, add chips for selected relation groups (entire categories selected)
		selectedRelations.forEach((relation: Relation) =>
		{
			const relationKey = relation.relationName.toLowerCase();

			tabs.push({
				name  : relationKey,
				label : relation.relationName === 'Company Internal Employee'
					? language.internalEmployees || 'Internal Employees'
					: relation.relationName,
				count   : relation.count,
				isGroup : true // This is a full relation group
			});
		});

		// Then, add chips for individual participants (grouped by relation)
		const grouped = getParticipantsByRelation();

		Object.keys(grouped).forEach((relationKey) =>
		{
			const relationParticipants = grouped[relationKey];

			// Skip if this relation is already selected as a group
			const isAlreadySelectedAsGroup = selectedRelations.some((r: Relation) =>
				r.relationName.toLowerCase() === relationKey
			);

			if (!isAlreadySelectedAsGroup)
			{
				const relation = relations.find((r: Relation) =>
					r.relationName.toLowerCase() === relationKey);

				// Handle "Others" tab for external emails
				if (relationKey === 'others')
				{
					tabs.push({
						name    : relationKey,
						label   : language?.others || 'Others',
						count   : relationParticipants.length,
						isGroup : false
					});
				}
				else
				{
					tabs.push({
						name  : relationKey,
						label : relation?.relationName === 'Company Internal Employee'
							? language.internalEmployees || 'Internal Employees'
							: relation?.relationName || relationKey,
						count   : relationParticipants.length,
						isGroup : false
					});
				}
			}
		});

		return tabs;
	};

	// Handle email input (manual add) - checks if email is not found in list
	const handleEmailAdd = (email: string) =>
	{
		if (!email)
		{
			setFormError({ email: language?.pleaseEnterEmail });

			return;
		}

		if (!ValidateEmail(email))
		{
			setFormError({ email: language?.pleaseEnterVaildEmail });

			return;
		}

		// Check if email is already selected
		if (selectedEmails.includes(email))
		{
			setFormError({ email: language?.emailAlreadyAdded });

			return;
		}

		// Check if email exists in the current participants list
		const emailExistsInList = participantsList.some((p: Participant) =>
		{
			const participantEmail = typeof p === 'string' ? p : (p?.email || p?.userId || '');

			return participantEmail.toLowerCase() === email.toLowerCase();
		});

		// Only add if email is not found in the list
		if (emailExistsInList)
		{
			setFormError({ email: language?.emailAlreadyInList || 'Email already exists in the list' });

			return;
		}

		const availableSeats = getAvailableSeats();

		if (availableSeats <= 0)
		{
			const errorMsg = language?.participantsCannotExceedTotalSeats || 'Participants cannot exceed total seats';

			setFormError({ participant: errorMsg });
			toast.info(errorMsg, { position: 'top-right', autoClose: 3000 });

			return;
		}

		// Add email to selectedEmails
		setSelectedEmails((prev) => [ ...prev, email ]);

		// Add participant with "Others" relation for external emails
		const externalParticipant: Participant = {
			email,
			name         : email.split('@')[0],
			relationName : 'Others',
			relationId   : ''
		};

		setSelectedParticipants((prev) => [ ...prev, externalParticipant ]);
		setFormError({});
	};

	// Handle tab change - Clear previous data and reload
	const handleTabChange = (tabName: string) =>
	{
		// Clear previous participants list immediately
		setParticipantsList([]);
		setPage(0);
		setSearchVal('');
		setHasMoreData(true); // Reset hasMoreData when switching tabs
		setActiveTab(tabName);
		// Reset loader ref to allow new pagination
		loaderRef.current = false;
	};

	// Get initials for avatar
	const getInitials = (name: string) =>
	{
		if (!name) return '??';

		const parts = name.split(' ');

		if (parts.length >= 2)
		{
			return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
		}

		return name.substring(0, 2).toUpperCase();
	};

	// Filter participants by search
	const filteredParticipants = participantsList.filter((p: Participant | string) =>
	{
		if (!searchVal) return true;

		const searchLower = searchVal.toLowerCase();
		const email = typeof p === 'string' ? p : (p?.email || '');
		const name = typeof p === 'string' ? '' : (p?.name || '');

		return email.toLowerCase().includes(searchLower) ||
			name.toLowerCase().includes(searchLower);
	});

	// Check if all visible participants are selected
	const areAllVisibleParticipantsSelected = () =>
	{
		if (filteredParticipants.length === 0) return false;

		return filteredParticipants.every((participant: Participant | string) =>
			isParticipantSelected(participant));
	};

	// Check if some (but not all) visible participants are selected
	const areSomeVisibleParticipantsSelected = () =>
	{
		if (filteredParticipants.length === 0) return false;

		const selectedCount = filteredParticipants.filter((participant: Participant | string) =>
			isParticipantSelected(participant)).length;

		return selectedCount > 0 && selectedCount < filteredParticipants.length;
	};

	// Handle select all toggle
	const handleSelectAll = async () =>
	{
		const allSelected = areAllVisibleParticipantsSelected();

		// Find the current relation
		const selectedRelation = relations.find((r: Relation) =>
		{
			const relationNameLower = r.relationName.toLowerCase();
			const activeTabLower = activeTab.toLowerCase();

			return relationNameLower === activeTabLower ||
				(activeTabLower === 'internal employees' && relationNameLower.includes('internal'));
		});

		if (!selectedRelation)
		{
			console.error('No relation found for activeTab:', activeTab);

			return;
		}

		if (allSelected)
		{
			// Deselect all participants from this relation
			// Remove the relation from selectedRelations
			setSelectedRelations((prev) => prev.filter((r: Relation) => 
				r.relationId !== selectedRelation.relationId
			));

			// Remove all participants from this relation
			setSelectedParticipants((prev) => prev.filter((p: Participant) =>
				p.relationId !== selectedRelation.relationId
			));

			// Remove emails of participants from this relation
			setSelectedEmails((prev) =>
			{
				const participantsToRemove = selectedParticipants
					.filter((p: Participant) => p.relationId === selectedRelation.relationId)
					.map((p: Participant) => p.email || p.userId || '');

				return prev.filter((email) => !participantsToRemove.includes(email));
			});

			// Remove from selectAllUsedForRelations
			setSelectAllUsedForRelations((prev) =>
			{
				const newMap = { ...prev };

				delete newMap[selectedRelation.relationId];

				return newMap;
			});
		}
		else
		{
			// Check if selecting all would exceed seat limit.
			const alreadySelectedFromRelation = selectedParticipants.filter((p: Participant) =>
				p.relationId === selectedRelation.relationId
			).length;

			const totalCount = getTotalSelectedCount() - alreadySelectedFromRelation + selectedRelation.count;

			if (totalCount > Number(noOfSeats))
			{
				const errorMsg = language.participantsCannotExceedTotalSeats || 'Participants cannot exceed total seats';

				setFormError({ participant: errorMsg });
				toast.info(errorMsg, { position: 'top-right', autoClose: 3000 });
				setTimeout(() => { setFormError({}); }, 3000);

				return;
			}

			// Add the entire relation group to selectedRelations
			setSelectedRelations((prev) =>
			{
				// Check if already selected
				const alreadySelected = prev.some((r: Relation) => r.relationId === selectedRelation.relationId);

				if (alreadySelected)
				{
					return prev;
				}

				return [ ...prev, selectedRelation ];
			});

			// Fetch ALL participants from this relation and add them
			try
			{
				const isInternalEmployees = selectedRelation.relationName.toLowerCase().includes('internal');
				let res: any;

				if (isInternalEmployees)
				{
					// Fetch all internal employees (use large limit to get all)
					const queryParamRes = queryParams(userData?.userId, userData?.companyId, userData?.empRole, userData?.role, 'get');
					let queryItem = `?status=approve&limit=1000&skip=0${queryParamRes}`;

					// Add department filter if active
					if (selectedDepartment && selectedDepartment !== '' && selectedDepartment !== 'All')
					{
						queryItem += `&departments=${encodeURIComponent(selectedDepartment)}`;
					}

					res = await getEmployeesData(queryItem);
				}
				else
				{
					// Fetch all participants from this relation
					const query: any = {
						relation : selectedRelation.relationId
					};

					res = await getConnectionswithRelationId(query);
				}

				if (res?.status && res?.data)
				{
					const participants = (res.data || []).map((item: any) => ({
						userId       : item.userId,
						name         : item.name || item.fullName || '',
						email        : item.email || item.userId,
						ava          : item.ava || item.profileImg || item.profileImage || item.profileImageUrl || '',
						department   : item.department || item.departmentName || '',
						type         : item.type || item.relationType || '',
						relationId   : selectedRelation.relationId,
						relationName : selectedRelation.relationName
					}));

					const existingKeys = selectedParticipants.flatMap((p) => participantKeys(p));
					const newParticipants = participants.filter((p: Participant) =>
						!participantKeys(p).some((k) => existingKeys.includes(k))
					);

					const newEmails = newParticipants
						.map((p: Participant) => p.email || p.userId)
						.filter((email): email is string => Boolean(email));

					// Add to selected emails and participants (avoid duplicates)
					setSelectedEmails((prev) =>
						[ ...prev, ...newEmails.filter((email) => !prev.includes(email)) ]);
					setSelectedParticipants((prev) => [ ...prev, ...newParticipants ]);

					// Mark that "Select All" was used for this relation
					setSelectAllUsedForRelations((prev) => ({
						...prev,
						[selectedRelation.relationId] : true
					}));

					console.log(`✅ Selected all ${participants.length} participants for: ${selectedRelation.relationName}`);
				}
			}
			catch (err)
			{
				console.error('Error fetching all participants:', err);
				catchErr(err);
			}
		}

		setFormError({});
	};

	// Update participantCount ref
	useEffect(() =>
	{
		if (participantCount?.current)
		{
			participantCount.current = [
				...selectedRelations,
				...selectedEmails
			];
		}
	}, [ selectedRelations, selectedEmails ]);

	// Set default tab for selected participants
	useEffect(() =>
	{
		if (selectedParticipants.length > 0 && !selectedParticipantsTab)
		{
			const tabs = getSelectedParticipantsTabs();

			if (tabs.length > 0)
			{
				setSelectedParticipantsTab(tabs[0].name);
			}
		}
	}, [ selectedParticipants.length ]);

	// Load relations on mount
	useEffect(() =>
	{
		getRelations();
	}, []);

	useEffect(() =>
	{
		if (activeTab && activeTab.toLowerCase().includes('internal'))
		{
			getDepartmentsList();
		}
	}, [ activeTab ]);

	// Initialize from shareTo metaData when relations are loaded
	useEffect(() =>
	{
		if (relations.length > 0 && shareTo && typeof shareTo === 'object')
		{
			console.log('🔍 Initializing from shareTo metaData:', {
				shareTo,
				relations : relations.map((r) => ({ name: r.relationName, id: r.relationId }))
			});

			const relationsArray = shareTo?.relations || [];
			const externalMembersArray = shareTo?.externalMembers || [];

			console.log('📋 Relations array:', relationsArray);
			console.log('📧 External members array:', externalMembersArray);

			// Process relations array
			if (relationsArray.length > 0)
			{
				const relationGroups: Relation[] = [];
				const individualParticipants: Participant[] = [];
				const departmentItems: Array<{ deptName: string; relationId: string; relationName: string }> = [];

				relationsArray.forEach((item: any) =>
				{
					const itemType = item?.type || item?.relationType || '';
					const isGroup = itemType === 'self-company-group' || itemType === 'relation-group';

					if (isGroup)
					{
						// This is a full relation group
						const matchingRelation = relations.find((r: Relation) =>
							r.relationId === (item.userId || item.relationId)
						);

						if (matchingRelation)
						{
							relationGroups.push(matchingRelation);
						}
					}
					else
					{
						// This is an individual participant (employee, company, department)
						let relationName = '';
						let relationId = '';

						if (itemType === 'self-company-employee' || itemType === 'self-company-department')
						{
							// Internal employee or department
							const internalRelation = relations.find((r: Relation) =>
								r.relationName.toLowerCase().includes('internal') ||
								r.relationName.toLowerCase() === 'company internal employee'
							);

							relationName = internalRelation?.relationName || 'Company Internal Employee';
							relationId = internalRelation?.relationId || '';

							if (itemType === 'self-company-department')
							{
								const deptName = item.name || '';

								if (deptName && !departmentItems.some((d) => d.deptName === deptName))
								{
									departmentItems.push({ deptName, relationId, relationName });
								}

								return;
							}
						}
						else if (itemType === 'relation-company')
						{
							// Individual company from a relation (vendor, partner, shareholder, etc.)
							// The 'name' field in the metadata contains the RELATION CATEGORY name (not company name)
							// For example: { name: "vendor", userId: "company123", type: "relation-company" }

							console.log('🔍 Processing relation-company:', item);

							// Try to match by the 'name' field (which is the relation category name)
							if (item?.name)
							{
								const matchingRelation = relations.find((r: Relation) =>
									r.relationName.toLowerCase() === item.name.toLowerCase()
								);

								if (matchingRelation)
								{
									relationName = matchingRelation.relationName;
									relationId = matchingRelation.relationId;
									console.log('✅ Matched by item.name:', { relationName, relationId });
								}
							}

							// If not found, try by relationName field
							if (!relationName && item?.relationName)
							{
								const matchingRelation = relations.find((r: Relation) =>
									r.relationName.toLowerCase() === item.relationName.toLowerCase()
								);

								if (matchingRelation)
								{
									relationName = matchingRelation.relationName;
									relationId = matchingRelation.relationId;
									console.log('✅ Matched by item.relationName:', { relationName, relationId });
								}
							}

							// If not found, try by relation field
							if (!relationName && item?.relation)
							{
								const matchingRelation = relations.find((r: Relation) =>
									r.relationName.toLowerCase() === item.relation.toLowerCase() ||
									r.relationId === item.relation
								);

								if (matchingRelation)
								{
									relationName = matchingRelation.relationName;
									relationId = matchingRelation.relationId;
									console.log('✅ Matched by item.relation:', { relationName, relationId });
								}
							}

							if (!relationName)
							{
								console.warn('⚠️ Could not match relation for item:', item);
								console.warn('Available relations:', relations.map((r) => r.relationName));
								relationName = 'Others';
								relationId = '';
							}
						}
						else
						{
							relationName = 'Others';
							relationId = '';
						}

						individualParticipants.push({
							userId     : item.userId || item.relationId || '',
							email      : item.email || item.userId || '',
							name       : item.name || '',
							ava        : item.ava || '',
							relationId,
							relationName,
							department : item.department || '',
							type       : itemType
						});
					}
				});

				console.log('✅ Processed relations:', { relationGroups, individualParticipants });

				// Set selected relation groups
				if (relationGroups.length > 0)
				{
					setSelectedRelations(relationGroups);

					// CRITICAL FIX: Fetch and select all participants from these relation groups
					// This ensures checkboxes are checked when user opens the relation tab
					relationGroups.forEach(async (relation: Relation) =>
					{
						try
						{
							const isInternalEmployees = relation.relationName.toLowerCase().includes('internal');
							let res: any;

							if (isInternalEmployees)
							{
								// Fetch all internal employees
								const queryParamRes = queryParams(userData?.userId, userData?.companyId, userData?.empRole, userData?.role, 'get');
								const queryItem = `?status=approve&limit=1000&skip=0${queryParamRes}`;

								res = await getEmployeesData(queryItem);
							}
							else
							{
								// Fetch all participants from this relation
								const query: any = {
									relation : relation.relationId
								};

								res = await getConnectionswithRelationId(query);
							}

							if (res?.status && res?.data)
							{
								const participants = (res.data || []).map((item: any) => ({
									userId       : item.userId,
									name         : item.name || item.fullName || '',
									email        : item.email || item.userId,
									ava          : item.ava || item.profileImg || item.profileImage || item.profileImageUrl || '',
									department   : item.department || item.departmentName || '',
									type         : item.type || item.relationType || '',
									relationId   : relation.relationId,
									relationName : relation.relationName
								}));

								const emails = participants
									.map((p: Participant) => p.email || p.userId)
									.filter((email): email is string => Boolean(email));

								// Add to selected emails and participants
								setSelectedEmails((prev) =>
								{
									const newEmails = emails.filter((email) => !prev.includes(email));

									return [ ...prev, ...newEmails ];
								});
								setSelectedParticipants((prev) =>
								{
									const existingKeys = prev.flatMap((p) => participantKeys(p));
									const newParticipants = participants.filter((p: Participant) =>
										!participantKeys(p).some((k) => existingKeys.includes(k))
									);

									return [ ...prev, ...newParticipants ];
								});

								console.log(`✅ Loaded ${participants.length} participants for relation group: ${relation.relationName}`);
							}
						}
						catch (err)
						{
							console.error(`Error loading participants for relation ${relation.relationName}:`, err);
							catchErr(err);
						}
					});
				}

				// Fetch and select ALL employees for each restored department,
				// so every employee of the department shows as checked by default
				if (departmentItems.length > 0)
				{
					departmentItems.forEach(async (deptItem) =>
					{
						try
						{
							if (!deptItem.deptName) return;

							const queryParamRes = queryParams(userData?.userId, userData?.companyId, userData?.empRole, userData?.role, 'get');
							const queryItem = `?status=approve&limit=1000&skip=0${queryParamRes}&departments=${encodeURIComponent(deptItem.deptName)}`;

							const res = await getEmployeesData(queryItem);

							if (res?.status && res?.data)
							{
								const participants = (res.data || []).map((emp: any) => ({
									userId       : emp.userId,
									name         : emp.name || emp.fullName || '',
									email        : emp.email || emp.userId,
									ava          : emp.ava || emp.profileImg || emp.profileImage || emp.profileImageUrl || '',
									department   : emp.department || emp.departmentName || deptItem.deptName,
									type         : 'self-company-employee',
									relationId   : deptItem.relationId,
									relationName : deptItem.relationName
								}));

								const emails = participants
									.map((p: Participant) => p.email || p.userId)
									.filter((email): email is string => Boolean(email));

								setSelectedEmails((prev) =>
								{
									const newEmails = emails.filter((email) => !prev.includes(email));

									return [ ...prev, ...newEmails ];
								});
								setSelectedParticipants((prev) =>
								{
									const existingKeys = prev.flatMap((p) =>
										[ p.userId, p.email ].filter((v): v is string => Boolean(v)));
									const newParticipants = participants.filter((p: Participant) =>
										!existingKeys.includes(p.userId || '') && !existingKeys.includes(p.email || '')
									);

									return [ ...prev, ...newParticipants ];
								});

								console.log(`✅ Loaded ${participants.length} employees for department: ${deptItem.deptName}`);
							}
						}
						catch (err)
						{
							console.error(`Error loading employees for department ${deptItem.deptName}:`, err);
							catchErr(err);
						}
					});
				}

				// Set individual participants from relations
				if (individualParticipants.length > 0)
				{
					const emails = individualParticipants
						.map((p) => p.email || p.userId)
						.filter((email): email is string => Boolean(email));

					setSelectedEmails((prev) => [ ...prev, ...emails ]);
					setSelectedParticipants((prev) => [ ...prev, ...individualParticipants ]);
				}
			}

			// Process externalMembers array - these go to "Others" chip
			if (externalMembersArray.length > 0)
			{
				console.log('📧 Processing external members:', externalMembersArray);

				const externalParticipants: Participant[] = [];
				const externalEmails: string[] = [];

				externalMembersArray.forEach((member: any) =>
				{
					const email = member?.email || member?.userId || member;

					if (email && !externalEmails.includes(email))
					{
						externalEmails.push(email);
						externalParticipants.push({
							email,
							userId       : member?.userId || email,
							name         : member?.name || (typeof email === 'string' && email.includes('@') ? email.split('@')[0] : email),
							ava          : member?.ava || '',
							relationName : 'Others',
							relationId   : '',
							department   : '',
							type         : ''
						});
					}
				});

				console.log('✅ Processed external members:', { externalEmails, externalParticipants });

				// Add external members to selected participants
				if (externalParticipants.length > 0)
				{
					setSelectedEmails((prev) => [ ...prev, ...externalEmails ]);
					setSelectedParticipants((prev) => [ ...prev, ...externalParticipants ]);
				}
			}
		}
	}, [ relations.length, JSON.stringify(shareTo) ]);

	// Load participants when popup opens and activeTab is set
	useEffect(() =>
	{
		if (open && activeTab)
		{
			// Clear list and reload when activeTab changes
			setParticipantsList([]);
			setPage(0);
			setHasMoreData(true); // Reset hasMoreData
			getParticipantsList(true);
		}
	}, [ open, activeTab ]);

	// Handle search
	useEffect(() =>
	{
		if (open && debouncedSearch !== undefined)
		{
			setPage(0);
			setHasMoreData(true);
			getParticipantsList(true);
		}
	}, [ debouncedSearch ]);

	// Handle pagination - only for Internal Employees with scroll
	useEffect(() =>
	{
		if (open && page > 0 && activeTab)
		{
			const selectedRelation = relations.find((r: Relation) =>
			{
				const relationNameLower = r.relationName.toLowerCase();
				const activeTabLower = activeTab.toLowerCase();

				return relationNameLower === activeTabLower ||
					(activeTabLower === 'internal employees' && relationNameLower.includes('internal'));
			});

			// Load the next page for any relation — Internal Employees via getEmployeesData
			// and other relations via getConnectionswithRelationId (both support skip/limit).
			if (selectedRelation)
			{
				getParticipantsList();
			}
		}
	}, [ page, activeTab ]);

	// Pagination observer
	useEffect(() =>
	{
		if (open)
		{
			LoadMoreList();
		}
	}, [ lastEleRef, participantsList.length, open ]);

	// Validate seats on change
	useEffect(() =>
	{
		if (seatRef.current !== noOfSeats)
		{
			seatRef.current = noOfSeats;
			const totalCount = getTotalSelectedCount();

			if (totalCount > Number(noOfSeats))
			{
				setFormError({ participant: language.participantsCannotExceedTotalSeats });
			}
			else
			{
				setFormError({});
			}
		}
	}, [ debouncedCount ]);

	// Initialize from participants prop (for collaboration mode or when no shareTo data)
	useEffect(() =>
	{
		// Only process participants if shareTo is empty (create mode or collaboration mode)
		const hasShareToData = shareTo && typeof shareTo === 'object' &&
			((shareTo?.relations && shareTo.relations.length > 0) ||
			(shareTo?.externalMembers && shareTo.externalMembers.length > 0));

		if (!hasShareToData && participants?.length > 0)
		{
			console.log('🔍 Initializing from participants prop (no shareTo data):', participants);

			const emails = participants.map((p: any) => p?.email ?? p);
			const participantsWithRelation: Participant[] = [];

			participants.forEach((p: any) =>
			{
				const email = p?.email ?? p;
				const isExternalEmail = typeof email === 'string' && email.includes('@');

				// If participant has relation info, use it; otherwise mark as "Others"
				if (p?.relationName && p?.relationId && !isExternalEmail)
				{
					participantsWithRelation.push({
						email,
						name         : p?.name || email.split('@')[0],
						ava          : p?.ava || '',
						relationId   : p.relationId,
						relationName : p.relationName,
						department   : p?.department || '',
						type         : p?.type || p?.relationType || ''
					});
				}
				else
				{
					// External email without relation info - mark as "Others"
					participantsWithRelation.push({
						email,
						name         : p?.name || (isExternalEmail ? email.split('@')[0] : email),
						ava          : p?.ava || '',
						relationName : 'Others',
						relationId   : '',
						department   : '',
						type         : ''
					});
				}
			});

			console.log('✅ Initialized from participants prop:', {
				emails,
				participantsWithRelation
			});

			setSelectedEmails(emails);
			setSelectedParticipants(participantsWithRelation);
		}
	}, [ JSON.stringify(participants), JSON.stringify(shareTo) ]);

	// Expose selected data via ref
	useImperativeHandle(ref, () => ({
		relations          : selectedRelations,
		emails             : selectedEmails,
		selectedParticipants,
		allRelations       : relations, // Expose all relations to check counts
		selectedDepartment,
		departmentsList,
		// Flag to indicate if user selected all visible participants (Select All was used)
		selectAllUsed      : areAllVisibleParticipantsSelected() && filteredParticipants.length > 0,
		// NEW: Track which relation tab currently has "Select All" active
		activeRelationTab  : activeTab,
		// NEW: Check if all visible participants on current tab are selected
		allVisibleSelected : areAllVisibleParticipantsSelected(),
		// NEW: Map of which relations had "Select All" used
		selectAllUsedForRelations
	}), [ selectedRelations, selectedEmails, selectedParticipants, relations, selectedDepartment, departmentsList, filteredParticipants, activeTab, selectAllUsedForRelations ]);

	// Get category tabs - No "All" tab, only relation tabs
	const getCategoryTabs = () =>
	{
		const tabs: Array<{ name: string; label: string; count: number }> = [];

		relations.forEach((rel: Relation) =>
		{
			tabs.push({
				name  : rel.relationName.toLowerCase(),
				label : rel.relationName === 'Company Internal Employee' ? language.internalEmployees || 'Internal Employees' : rel.relationName,
				count : rel.count
			});
		});

		return tabs;
	};

	const categoryTabs = getCategoryTabs();
	const isInternalEmployeesTab = activeTab === 'internal employees' || activeTab.includes('internal');
	const internalEmployeesRelation = relations.find((r: Relation) =>
		r.relationName.toLowerCase().includes('internal'));

	// Handle department change - reload when department selection changes
	useEffect(() =>
	{
		if (open && activeTab && isInternalEmployeesTab)
		{
			// Reset and reload when department changes
			setPage(0);
			setHasMoreData(true);
			setParticipantsList([]);
			loaderRef.current = false;
			getParticipantsList(true);
		}
	}, [ selectedDepartment, open, activeTab ]);

	return (
		<>
			{/* Main Input Field */}
			<Grid container>
				<Grid item xs={12}>
					<Typography my='var(--pixel8)' fontWeight='var(--font500)'>
						{language?.addParticipants || 'Add Participants'}{' '}
						<Typography component='span' fontWeight='var(--font500)'>
							{`${getTotalSelectedCount()} / ${noOfSeats ?? 0}`}
						</Typography>
					</Typography>
					{/* Selected Participants Section with Tabs */}
					{/* {selectedParticipants.length > 0 && (
						<Grid item xs={12} mt={2}>
							<Box sx={{ border: '1px solid #E5E7EB', borderRadius: '8px', p: 2, backgroundColor: '#F9FAFB' }}> */}
					{/* Header */}
					{/* <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
									<Typography variant='body1' fontWeight='var(--font500)'>
										{language?.selectedParticipants || 'Selected Participants'}{' '}
										<Typography component='span' fontWeight='var(--font500)' color='text.secondary'>
											({selectedParticipants.length} {language?.selected || 'selected'})
										</Typography>
									</Typography>
								</Stack> */}

					{/* Relation Chips for Selected Participants */}
					{getSelectedParticipantsTabs().length > 0 && (
						<Stack
							direction='row'
							spacing={1}
							sx={{ mb: 2, overflowX: 'hidden', scrollbarGutter: 'stable', '&:hover': { overflowX: 'auto' }, flexWrap: 'nowrap' }}
						>
							{getSelectedParticipantsTabs().map((tab) =>
							{
								// const isSelected = selectedParticipantsTab === tab.name ||
								// 				(!selectedParticipantsTab && tab.name === getSelectedParticipantsTabs()[0]?.name);

								return (
									<Button
										key={tab.name}
										className='capitalTxt'
										onClick={(e) =>
										{
											setSelectedRelationForDialog(tab.name);
											setAnchorEl(e.currentTarget);
										}}
										variant='outlined'
										startIcon={<MdOutlinePeople />}
										sx={{
											backgroundColor : '#FAF5FF',
											color           : 'var(--primaryColor)',
											border          : '1px solid #E9D4FF',
											borderRadius    : '10px',
											textTransform   : 'none',
											fontWeight      : 500,
											px              : 1.5,
											cursor          : 'pointer',
											'&:hover'       : {
												backgroundColor : '#F3E8FF',
												borderColor     : '#D8B4FE'
											}
										}}
									>
										{`${tab.label} (${tab.count})`}
									</Button>

								);
							})}
						</Stack>
					)}

					{/* Selected Participants Popover */}
					<Popover
						open={Boolean(anchorEl)}
						anchorEl={anchorEl}
						onClose={() => setAnchorEl(null)}
						anchorOrigin={{
							vertical   : 'bottom',
							horizontal : 'left'
						}}
						transformOrigin={{
							vertical   : 'top',
							horizontal : 'left'
						}}
						PaperProps={{
							sx : {
								mt           : 1,
								minWidth     : '300px',
								maxWidth     : '400px',
								maxHeight    : '400px',
								borderRadius : '8px',
								boxShadow    : '0px 4px 12px rgba(0, 0, 0, 0.15)'
							}
						}}
					>
						<Box sx={{ p: 2 }}>
							{/* Header */}
							<Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
								<Typography variant='h6' fontWeight='var(--font500)'>
									{capitalizeWords(getSelectedParticipantsTabs().find((tab) => tab.name === selectedRelationForDialog)?.label || 'Selected Participants')}
								</Typography>
								{/* Close button for relation groups */}
								{(() =>
								{
									const selectedTab = getSelectedParticipantsTabs().find((tab) => tab.name === selectedRelationForDialog);
									const isRelationGroup = selectedTab?.isGroup;

									if (isRelationGroup)
									{
										return (
											<IconButton
												size='small'
												onClick={() =>
												{
													// Find and deselect the relation group
													const relationToRemove = selectedRelations.find((r: Relation) =>
														r.relationName.toLowerCase() === selectedRelationForDialog
													);

													if (relationToRemove)
													{
														handleRelationToggle(relationToRemove);
													}

													setAnchorEl(null);
												}}
												sx={{ cursor: 'pointer' }}
											>
												<IoClose />
											</IconButton>
										);
									}

									return null;
								})()}
							</Stack>

							{/* Participants List or Group Message */}
							<Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
								{(() =>
								{
									const selectedTab = getSelectedParticipantsTabs().find((tab) => tab.name === selectedRelationForDialog);
									const isRelationGroup = selectedTab?.isGroup;

									// If this is a relation group (entire category selected)
									if (isRelationGroup)
									{
										return (
											<Box sx={{ p: 2, textAlign: 'center' }}>
												<Typography variant='body1' fontWeight='var(--font500)' color='var(--primaryColor)'>
													{language?.allSelected || 'All Participants Selected'}
												</Typography>
												<Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
													{`${selectedTab.count} ${language?.participantsSelected || 'participants selected'}`}
												</Typography>
												<Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
													{language?.clickCloseToDeselect || 'Click the X button to deselect this group'}
												</Typography>
											</Box>
										);
									}

									// Otherwise, show individual participants
									const participants = getParticipantsByRelation()[selectedRelationForDialog];

									if (participants && participants.length > 0)
									{
										return participants.map((participant: Participant, index: number) =>
										{
											const identifier = participant.email || participant.userId || '';
											const name = participant.name || identifier.split('@')[0];
											const avatar = participant.ava || '';

											return (
												<Stack
													key={`${identifier}-${index}`}
													direction='row'
													alignItems='center'
													justifyContent='space-between'
													sx={{
														padding        : '0.75rem',
														borderBottom   : '1px solid #e0e0e0',
														'&:last-child' : {
															borderBottom : 'none'
														}
													}}
												>
													<Stack direction='row' alignItems='center' sx={{ flexGrow: 1 }}>
														<Avatar
															src={avatar}
															sx={{
																height : 'var(--pixel40)',
																width  : 'var(--pixel40)',
																mr     : 1.5
															}}
														>
															{getInitials(name)}
														</Avatar>
														<Stack>
															<Typography variant='body1' fontWeight='var(--font500)'>
																{name}
															</Typography>
															<Typography variant='body2' color='text.secondary'>
																{identifier.includes('@') ? identifier : name}
															</Typography>
														</Stack>
													</Stack>
													<IconButton
														onClick={() =>
														{
															handleParticipantDelete(participant);
															// Close popover if no participants left in this relation
															const remainingParticipants = getParticipantsByRelation()[selectedRelationForDialog]?.filter((p) =>
																(p.email || p.userId) !== identifier);

															if (remainingParticipants?.length === 0)
															{
																setAnchorEl(null);
															}
														}}
														sx={{ cursor: 'pointer' }}
													>
														<IoClose />
													</IconButton>
												</Stack>
											);
										});
									}

									return (
										<Typography variant='body2' color='text.secondary' sx={{ p: 2, textAlign: 'center' }}>
											{language?.nodataFound || 'No participants selected'}
										</Typography>
									);
								})()}
							</Box>
						</Box>
					</Popover>
					{/* </Box>
						</Grid>
					)} */}
				</Grid>
				<Grid item xs={12} >
					<TextField
						fullWidth
						variant='outlined'
						placeholder={language?.addParticipants || 'Add participants'}
						size='small'
						margin='dense'
						value=''
						inputProps={{ readOnly: true }}
						onClick={() => setOpen((prev) => !prev)}
						InputProps={{
							startAdornment : (
								<InputAdornment position='start'>
									<Box sx={{ cursor: 'pointer', display: 'flex' }}>
										<MdOutlinePeople color='primary' />
									</Box>
								</InputAdornment>
							),
							sx : { cursor: 'pointer', border: '1px solid #D1D5DC', borderRadius: '8px' }
						}}
					/>
					{(formError?.email || formError?.participant) && (
						<FormHelperText id='txtEmail-error' className='errorTxt'>
							{formError?.email || formError?.participant}
						</FormHelperText>
					)}
				</Grid>

				{/* Selected Chips */}

			</Grid>

			{/* Participants Selection - Inline Expandable Section */}
			<Collapse in={open} timeout='auto' unmountOnExit sx={{ overflowY: 'auto', width: '100%' }} >
				<Box sx={{ mt: 2, p: 2, backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #E5E7EB' }}>
					{/* Header */}
					{/* <Stack direction='row' justifyContent='space-between' alignItems='center' mb={2}>
						<Typography variant='h6' fontWeight='var(--font500)'>
							{language?.addParticipants || 'Add Participants'} ({getTotalSelectedCount()}/{noOfSeats})
						</Typography>
						<Button size='small' onClick={() => setOpen(false)}>
							{language?.close || 'Close'}
						</Button>
					</Stack> */}

					{/* Search Bar */}
					<TextField
						fullWidth
						placeholder={language?.searchParticipants || 'Search by name, email, or department (Press Enter to add email if not found)'}
						size='small'
						variant='outlined'
						value={searchVal}
						onChange={(e) => setSearchVal(e.target.value.replace(/^\s+/, ''))}
						onKeyDown={(e) =>
						{
							if (e.key === 'Enter' && searchVal)
							{
								e.preventDefault();
								// Check if search value is a valid email
								if (ValidateEmail(searchVal))
								{
									handleEmailAdd(searchVal);
									// Clear search after adding email
									setSearchVal('');
								}
							}
						}}
						InputProps={{
							startAdornment : (
								<InputAdornment position='start'>
									<SearchIcon />
								</InputAdornment>
							),
							endAdornment : searchVal && ValidateEmail(searchVal) && (
								<InputAdornment position='end'>
									<IconButton
										size='small'
										onClick={() =>
										{
											handleEmailAdd(searchVal);
											setSearchVal('');
										}}
										sx={{
											cursor    : 'pointer',
											color     : 'var(--primaryColor)',
											'&:hover' : {
												backgroundColor : 'rgba(0, 0, 0, 0.04)'
											}
										}}
									>
										<AddIcon />
									</IconButton>
								</InputAdornment>
							)
						}}
						sx={{ mb: 2 }}
					/>

					{/* Category Tabs */}
					<Stack direction='row' spacing={1} sx={{ mb: 2, overflowX: 'hidden', scrollbarGutter: 'stable', '&:hover': { overflowX: 'auto' }, flexWrap: 'nowrap' }} >
						{categoryTabs.map((tab) => (
							<Chip
								key={tab.name}
								label={`${tab.label} (${tab.count})`}
								onClick={() => handleTabChange(tab.name)}
								variant={activeTab === tab.name ? 'filled' : 'outlined'}
								sx={{
									backgroundColor : activeTab === tab.name ? 'var(--primaryColor)' : 'transparent',
									color           : activeTab === tab.name ? 'white' : 'black',
									borderColor     : activeTab === tab.name ? 'transparent' : '#666666',
									cursor          : 'pointer'
								}}
							/>
						))}
					</Stack>

					{/* Department Chips (for Internal Employees) - Only one can be selected */}
					{isInternalEmployeesTab && internalEmployeesRelation && (
						<Stack direction='row' spacing={1} sx={{ mb: 2, overflowX: 'hidden', scrollbarGutter: 'stable', '&:hover': { overflowX: 'auto' }, flexWrap: 'nowrap' }} >
							<Chip
								label='All'
								onClick={() =>
								{
									// Clear department selection - useEffect will handle reload
									setSelectedDepartment('');
								}}
								variant={selectedDepartment === '' ? 'filled' : 'outlined'}
								sx={{
									backgroundColor : selectedDepartment === '' ? 'var(--primaryColor)' : 'transparent',
									color           : selectedDepartment === '' ? 'white' : 'black',
									borderColor     : selectedDepartment === '' ? 'transparent' : '#666666',
									cursor          : 'pointer'
								}}
							/>
							{departmentsList.map((dept: any) =>
							{
								const deptName = dept.name || dept;
								const isSelected = selectedDepartment === deptName;

								return (
									<Chip
										key={dept._id || deptName}
										label={deptName}
										onClick={() => handleDepartmentToggle(deptName)}
										variant={isSelected ? 'filled' : 'outlined'}
										sx={{
											backgroundColor : isSelected ? 'var(--primaryColor)' : 'transparent',
											color           : isSelected ? 'white' : 'black',
											borderColor     : isSelected ? 'transparent' : '#666666',
											cursor          : 'pointer'

										}}
									/>
								);
							})}
						</Stack>
					)}

					{/* Select All Checkbox - Only for relations and internal employees */}
					{filteredParticipants.length > 0 && (
						<Stack
							direction='row'
							alignItems='center'
							sx={{
								padding      : '0.75rem',
								borderBottom : '1px solid #e0e0e0',
								cursor       : 'pointer',
								'&:hover'    : {
									backgroundColor : '#f5f5f5'
								}
							}}
							onClick={handleSelectAll}
						>
							<Checkbox
								sx={{
									color           : 'var(--primaryColor)',
									'&.Mui-checked' : {
										color : 'var(--primaryColor)'
									}
								}}
								checked={areAllVisibleParticipantsSelected()}
								indeterminate={areSomeVisibleParticipantsSelected()}
								onChange={(e) =>
								{
									e.preventDefault();
									e.stopPropagation();
									handleSelectAll();
								}}
								onClick={(e) =>
								{
									e.preventDefault();
									e.stopPropagation();
									handleSelectAll();
								}}
							/>
							<Typography variant='body1' sx={{ ml: 1, fontWeight: 500 }}>
								{language?.selectAll || 'Select All'}
							</Typography>
							<Typography variant='body2' color='text.secondary' sx={{ ml: 1 }}>
								({filteredParticipants.length} {language?.items || 'items'})
							</Typography>
						</Stack>
					)}

					{/* Participants List */}
					<Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
						{loading && <Loading />}
						{!loading && filteredParticipants.length === 0 && (
							<NoData
								noDataHead={language?.nodataFound || 'No data found'}
								color='lightTxt'
								src1='/images/Bpsnodata.svg'
							/>
						)}
						{!loading && filteredParticipants.length > 0 && areAllVisibleParticipantsSelected() && (
							<Box sx={{ p: 3, textAlign: 'center' }}>
								<Typography variant='body1' fontWeight='var(--font500)' color='var(--primaryColor)'>
									{language?.allSelected || 'All Participants Selected'}
								</Typography>
								<Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
									{(() =>
									{
										// Find the current relation to get total count
										const currentRelation = relations.find((r: Relation) =>
										{
											const relationNameLower = r.relationName.toLowerCase();
											const activeTabLower = activeTab.toLowerCase();

											return relationNameLower === activeTabLower ||
												(activeTabLower === 'internal employees' && relationNameLower.includes('internal'));
										});

										// If department filter is active, show department count
										if (selectedDepartment && selectedDepartment !== '' && selectedDepartment !== 'All')
										{
											const department = departmentsList.find((d: any) => (d.name || d) === selectedDepartment);
											const deptCount = department?.employeeCount || department?.count || filteredParticipants.length;

											return `${deptCount} ${language?.participantsSelected || 'participants selected'}`;
										}

										// Otherwise show relation total count
										const totalCount = currentRelation?.count || filteredParticipants.length;

										return `${totalCount} ${language?.participantsSelected || 'participants selected'}`;
									})()}
								</Typography>
							</Box>
						)}
						{!loading && filteredParticipants.length > 0 && !areAllVisibleParticipantsSelected() && filteredParticipants.map((participant: Participant | string, index: number) =>
						{
							// Handle both string emails/userIds and Participant objects
							let identifier = '';
							let name = '';
							let avatar = '';

							if (typeof participant === 'string')
							{
								identifier = participant;
								name = identifier.includes('@') ? identifier.split('@')[0] : identifier;
							}
							else if (participant && typeof participant === 'object')
							{
								identifier = participant.email || participant.userId || '';
								name = participant.name || (identifier.includes('@') ? identifier.split('@')[0] : identifier);
								avatar = participant.ava || '';
							}
							else
							{
								return null;
							}

							if (!identifier || typeof identifier !== 'string')
							{
								return null;
							}

							const displayName = name || (identifier.includes('@') ? identifier.split('@')[0] : identifier);
							const loginUserId = userData?.userId;
							const isLoginUser = Boolean(loginUserId) && (
								typeof participant === 'string'
									? participant === loginUserId
									: participant?.userId === loginUserId
							);

							const finalIsSelected = isParticipantSelected(participant);

							return (
								<Stack
									key={`${identifier}-${index}`}
									direction='row'
									alignItems='center'
									sx={{
										padding      : '0.75rem',
										borderBottom : '1px solid #e0e0e0',
										cursor       : isLoginUser ? 'not-allowed' : 'pointer',
										opacity      : isLoginUser ? 0.6 : 1,
										'&:hover'    : isLoginUser
											? {}
											: {
												backgroundColor : '#f5f5f5'
											}
									}}
									onClick={(e) =>
									{
										e.preventDefault();
										e.stopPropagation();
										if (!isLoginUser)
										{
											handleParticipantToggle(participant);
										}
									}}
								>
									<Checkbox
										sx={{
											color           : 'var(--primaryColor)',
											'&.Mui-checked' : {
												color : 'var(--primaryColor)'
											}
									}}
										checked={finalIsSelected}
										disabled={isLoginUser}
										onChange={(e) =>
										{
											e.preventDefault();
											e.stopPropagation();
											if (!isLoginUser)
											{
												handleParticipantToggle(participant);
											}
										}}
										onClick={(e) =>
										{
											e.preventDefault();
											e.stopPropagation();
											if (!isLoginUser)
											{
												handleParticipantToggle(participant);
											}
										}}
									/>
									<Avatar
										src={avatar}
										sx={{
											height : 'var(--pixel40)',
											width  : 'var(--pixel40)',
											mr     : 1
										}}
									>
										{getInitials(displayName)}
									</Avatar>
									<Stack sx={{ flexGrow: 1 }}>
										<Typography variant='body1'>{displayName}</Typography>
										<Typography variant='body2' color='text.secondary'>
											{identifier.includes('@') ? identifier : name}
										</Typography>
									</Stack>
									{index === filteredParticipants.length - 2 && <span ref={lastEleRef} />}
								</Stack>
							);
						})}
					</Box>

					{/* Footer Actions */}
					{/* <Stack direction='row' justifyContent='flex-end' spacing={2} mt={2}>
						<Button variant='outlined' size='small' onClick={() => setOpen(false)}>
							{language?.cancel || 'Cancel'}
						</Button>
						<Button
							variant='contained'
							size='small'
							onClick={() =>
							{
								setFormError({});
								setOpen(false);
							}}
							sx={{ backgroundColor: 'var(--primaryColor)', color: 'white' }}
						>
							{language?.add || 'Add'}
						</Button>
					</Stack> */}
				</Box>
			</Collapse>
		</>
	);
});

ParticipantsSelector.displayName = 'ParticipantsSelector';

export default ParticipantsSelector;
